import { Injectable } from '@angular/core'
import { Observable, Subscription } from 'rxjs'
import { take } from 'rxjs/operators'
import { Footnote } from 'src/app/models/footnote.model'
import {
  ProfileBody,
  ProfileDashboardModel,
} from 'src/app/models/profile-response.model'
import { AEMChunk } from 'src/app/services/AEM/aem-chunk.model'
import { CentralContentService } from 'src/app/services/content/central-content.service'
import { FootnoteService } from 'src/app/services/footnotes/footnotes.service'
import { MainService } from 'src/app/services/main.service'

import { Dashboard } from './dashboard.model'

const riskIndex = 0
const minIndex = 1
const perfIndex = 2
const priceIndex = 3
const expenseIndex = 4
const contentIndex = 5
const marketPriceContentIndex = 6

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  alsoAvailableLinksETF: string
  alsoAvailableLinksAdmiral: string
  alsoAvailableLinksInvestors: string // lower-minimum
  alsoAvailableLinksLowerAdmiral: string
  alsoAvailableLinksLowerETF: string
  alsoAvailableLinksEtfAndLowerAdmiral: string
  alsoAvailableLinksEtfAndLowerInvestors: string
  alsoAvailableLinksInvestorsMutualFunds: string

  dashboardModel: Dashboard
  profileModel: ProfileBody
  profileDashboardModel: ProfileDashboardModel
  InvestorAdmiralShareSubTitle = ''
  contentModel$: Observable<any>

  // AEM content Vars
  expenseRatio: AEMChunk
  acquiredFundFeesAndExpenses: AEMChunk
  aemContentSub: Subscription
  secYieldNonVgContent: AEMChunk
  secYieldNonVgFootNote: Footnote

  constructor(
    private readonly mainService: MainService,
    private readonly footnoteService: FootnoteService,
    private readonly centralContentService: CentralContentService,
  ) {}

  getProfileDashboardModel(): Observable<ProfileDashboardModel> {
    return new Observable((subscriber) => {
      this.mainService
        .getProfileObservable()
        .pipe(take(1))
        .subscribe((profObs) => {
          if (
            Object.keys(profObs).length !== 0 &&
            profObs[0]?.name !== 'HTTPError' &&
            this.profileDashboardModel === undefined
          ) {
            const profPos = profObs?.fundProfile
            this.profileModel = profPos
            this.profileDashboardModel = {
              fundName: profPos?.longName,
              fundId: profPos?.fundId,
              ticker: profPos?.ticker,
              isETF: profPos?.isETF,
              is529: profPos?.is529,
              isMutualFund: profPos?.isMutualFund,
              isCitFund: profPos?.fundFact?.isCitFund,
              isFundTransferableToVGI:
                profPos?.fundFact?.isFundTransferableToVGI,
              isFundAccessFund: profPos?.fundFact?.isFundAccessFund,
              isFundOfFunds: profPos?.fundFact?.isFundOfFunds,
              managementStyle: profPos?.fundManagementStyle,
              assetClass: profPos?.type,
              category: profPos?.category,
              expenseRatio: profPos?.expenseRatio,
              expenseRatioAsOfDate: profPos?.expenseRatioAsOfDate,
              isClosed: profPos?.fundFact?.isClosed,
              isClosedToNewInvestors: profPos?.fundFact?.isClosedToNewInvestors,
              isMarketNeutralFund: profPos?.fundFact?.isMarketNeutralFund,
              fundCategoryId: profPos?.fundCategory?.low?.id,
              isInternalFund: profPos?.isInternalFund,
              isExternalFund: profPos?.isExternalFund,
              longName: profPos?.longName,
              isMoneyMarket: profPos?.fundFact.isMoneyMarket,
              isNTFFund: profPos?.fundFact?.isNTFFund,
              isLoadFund: profPos?.fundFact?.isLoadFund,
              hasTransactionFee: profPos?.fundFact?.hasTransactionFee,
            }
          }
          subscriber.next(this.profileDashboardModel)
          subscriber.complete()
        })
    })
  }
  getDashboardModelData(): Observable<Dashboard> {
    return new Observable((subscriber) => {
      this.mainService
        .getDashboardObservable()
        .pipe(take(1))
        .subscribe(
          // eslint-disable-next-line complexity
          (dbObs) => {
            if (
              Object.keys(dbObs).length !== 0 &&
              dbObs[0]?.name !== 'HTTPError' &&
              this.dashboardModel === undefined
            ) {
              const riskPos = dbObs[riskIndex]
              const minPos = dbObs[minIndex]
              const perfPos = dbObs[perfIndex]?.YTD
              const pricePos = dbObs[priceIndex]?.currentPrice
              const expensePos = dbObs[expenseIndex]
              const marketPricePos = dbObs[marketPriceContentIndex]
              this.dashboardModel = {
                yield: pricePos?.yield?.yieldPct,
                prospectusExpenseRatio: expensePos?.prospectusExpenseRatio,
                prospectusExpenseRatioAsOfDate:
                  expensePos?.prospectusExpenseRatioAsOfDate,
                riskCode: riskPos?.code,
                ytdRegular: perfPos?.regular,
                ytdMarket: perfPos?.marketPrice,
                ytdAsOfDate: perfPos?.asOfDate,
                navPrice: pricePos?.dailyPrice?.regular.price,
                navPriceAsOfDate: pricePos?.dailyPrice?.regular?.asOfDate,
                marketPrice: pricePos?.dailyPrice?.market?.price,
                marketPriceAsOfDate: pricePos?.dailyPrice?.market?.asOfDate,
                minimum: minPos?.general?.initial,
                unitPrice: pricePos?.dailyPrice?.regular?.price,
                unitPriceAsOfDate: pricePos?.dailyPrice?.regular?.asOfDate,
                priceChangeAmount:
                  pricePos?.dailyPrice?.regular.priceChangeAmount,
                marketPriceChangeAmount:
                  pricePos?.dailyPrice?.market?.priceChangeAmount,
                yieldNote: pricePos?.yield?.yieldNote,
                asOfDate: pricePos?.yield?.asOfDate,
                contentMarketNeutralFootNote:
                  dbObs[contentIndex]?.marketNeutralFootNote,
                closingMarketPrice: marketPricePos['@closingMarketPrice'],
              }
              this.setFootnotes()
              this.contentModel$ = this.centralContentService.aemContent$
            }
            subscriber.next(this.dashboardModel)
            subscriber.complete()
          },
        )
    })
  }
  private setFootnotes() {
    this.aemContentSub = this.centralContentService.aemContent$.subscribe(
      (content) => {
        this.parseAEMContent(content)
        if (this.profileModel.isExternalFund && !this.profileModel.isETF) {
          this.secYieldNonVgContent =
            this.centralContentService.getContentByBody(
              'footnote_non_vanguard_sec_yield',
              content,
            )
          this.dashboardModel.secYieldNonVgFootNote = new Footnote(
            this.secYieldNonVgContent.contentValue,
            'dashboard',
            true,
            'footnote_non_vanguard_sec_yield',
          )
          this.footnoteService.addFootnote(
            this.dashboardModel.secYieldNonVgFootNote,
          )
        }
      },
    )
    if (
      this.profileDashboardModel.isFundOfFunds &&
      this.profileDashboardModel.isMutualFund
    ) {
      this.dashboardModel.expenseRatioFootnote = new Footnote(
        this.acquiredFundFeesAndExpenses.contentValue,
        'dashboard',
        this.acquiredFundFeesAndExpenses.isAEM,
        'footnote_acquired_fund_fees_and_expenses',
      )
      this.footnoteService.addFootnote(this.dashboardModel.expenseRatioFootnote)
    } else {
      if (
        this.profileDashboardModel.isMarketNeutralFund ||
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        this.profileDashboardModel.fundCategoryId === 1416
      ) {
        this.dashboardModel.expenseRatioFootnote = new Footnote(
          this.expenseRatio.contentValue,
          'dashboard',
          this.expenseRatio.isAEM,
          'footnote_expense_ratio',
        )
        this.footnoteService.addFootnote(
          this.dashboardModel.expenseRatioFootnote,
        )
      }
    }
  }
  parseAEMContent(content) {
    if (
      this.profileDashboardModel.ticker === 'VASFX' ||
      this.profileDashboardModel.ticker === 'VMNIX' ||
      this.profileDashboardModel.ticker === 'VMNFX'
    ) {
      this.expenseRatio = {
        contentValue: this.dashboardModel.contentMarketNeutralFootNote,
        isAEM: true,
      }
    } else {
      this.expenseRatio = this.centralContentService.getContentByBody(
        'footnote_expense_ratio',
        content,
      )
    }
    this.acquiredFundFeesAndExpenses =
      this.centralContentService.getContentByBody(
        'footnote_acquired_fund_fees_and_expenses',
        content,
      )
  }

  getValidatedSubtitle(jsonContent: any) {
    this.alsoAvailableLinksETF = this.centralContentService.getContentByBody(
      'banner_also_available_links_etf',
      jsonContent,
    )?.contentValue
    this.alsoAvailableLinksAdmiral =
      this.centralContentService.getContentByBody(
        'banner_also_available_links_admiral',
        jsonContent,
      )?.contentValue
    this.alsoAvailableLinksInvestors =
      this.centralContentService.getContentByBody(
        'banner_also_available_links_investors',
        jsonContent,
      )?.contentValue
    this.alsoAvailableLinksLowerAdmiral =
      this.centralContentService.getContentByBody(
        'banner_also_available_links_lower_admiral',
        jsonContent,
      )?.contentValue
    this.alsoAvailableLinksLowerETF =
      this.centralContentService.getContentByBody(
        'banner_also_available_links_lower_etf',
        jsonContent,
      )?.contentValue
    this.alsoAvailableLinksEtfAndLowerAdmiral =
      this.centralContentService.getContentByBody(
        'banner_also_available_links_etf_and_lower_admiral',
        jsonContent,
      )?.contentValue
    this.alsoAvailableLinksEtfAndLowerInvestors =
      this.centralContentService.getContentByBody(
        'banner_also_available_links_etf_and_lower_investors',
        jsonContent,
      )?.contentValue
    this.alsoAvailableLinksInvestorsMutualFunds =
      this.centralContentService.getContentByBody(
        'banner_also_available_links_investors_mutual_funds',
        jsonContent,
      )?.contentValue

    let subTitle = ''

    subTitle = this.getInvestorAdmiralShareSubTitle(this.profileModel)
    if (!subTitle) {
      subTitle = this.getETFAssociatedIDsSubTitle(this.profileModel)
    }
    return subTitle
  }

  // eslint-disable-next-line complexity, max-statements
  getInvestorAdmiralShareSubTitle(profile: ProfileBody): string {
    if (
      profile?.fundFact?.isInstitutionalShare &&
      !this.isInstitutionalPlusFundId(profile) &&
      profile.associatedFundTickers?.admiralFundId
    ) {
      this.InvestorAdmiralShareSubTitle =
        this.alsoAvailableLinksLowerAdmiral.replace(
          'ticker',
          profile.associatedFundTickers.admiralFundId.toLowerCase(),
        )
    }

    this.isAdmiralShare(profile)

    if (profile?.fundFact?.isInvestorShare && profile.fundId !== '0030') {
      if (
        profile.associatedFundTickers?.admiralFundId &&
        profile.associatedFundTickers?.etfFundId
      ) {
        this.InvestorAdmiralShareSubTitle =
          this.alsoAvailableLinksEtfAndLowerAdmiral
            .replace(
              'tickerETF',
              profile.associatedFundTickers.etfFundId.toLowerCase(),
            )
            .replace(
              'tickerAdmiral',
              profile.associatedFundTickers.admiralFundId.toLowerCase(),
            )
      } else if (profile.associatedFundTickers?.admiralFundId) {
        this.InvestorAdmiralShareSubTitle =
          this.alsoAvailableLinksLowerAdmiral.replace(
            'ticker',
            profile.associatedFundTickers.admiralFundId.toLowerCase(),
          )
      } else if (profile.associatedFundTickers?.etfFundId) {
        this.InvestorAdmiralShareSubTitle =
          this.alsoAvailableLinksLowerETF.replace(
            'ticker',
            profile.associatedFundTickers.etfFundId,
          )
      }
    }

    this.isInstitutionShare(profile)

    return this.InvestorAdmiralShareSubTitle
  }

  // eslint-disable-next-line complexity, max-statements
  isAdmiralShare(profile: ProfileBody) {
    if (profile?.fundFact?.isAdmiralShare) {
      if (
        profile.associatedFundTickers?.investorFundId &&
        profile.associatedFundTickers.etfFundId
      ) {
        this.InvestorAdmiralShareSubTitle = this.alsoAvailableLinksETF.replace(
          'ticker',
          profile.associatedFundTickers.etfFundId.toLowerCase(),
        )
      }

      if (
        profile.associatedFundTickers?.investorFundId &&
        !profile.associatedFundTickers.etfFundId &&
        profile.fundId !== '0066'
      ) {
        if (profile.fundId === '0502' || profile.fundId === '0513') {
          this.InvestorAdmiralShareSubTitle = ''
        } else {
          this.InvestorAdmiralShareSubTitle =
            this.alsoAvailableLinksInvestors.replace(
              'ticker',
              profile.associatedFundTickers.investorFundId.toLowerCase(),
            )
        }
      }
      if (
        !profile.associatedFundTickers?.investorFundId &&
        profile.associatedFundTickers?.etfFundId
      ) {
        this.InvestorAdmiralShareSubTitle = this.alsoAvailableLinksETF.replace(
          'ticker',
          profile.associatedFundTickers.etfFundId.toLowerCase(),
        )
      }
    }
  }

  // eslint-disable-next-line complexity, max-statements
  isInstitutionShare(profile: ProfileBody) {
    if (
      profile?.fundFact?.isInstitutionalShare &&
      !this.isInstitutionalPlusFundId(profile)
    ) {
      if (
        profile.associatedFundTickers?.etfFundId &&
        !profile.associatedFundTickers?.investorFundId &&
        !profile.associatedFundTickers?.admiralFundId
      ) {
        this.InvestorAdmiralShareSubTitle = this.alsoAvailableLinksETF.replace(
          'ticker',
          profile.associatedFundTickers.etfFundId.toLowerCase(),
        )
      }
      if (
        !profile.associatedFundTickers?.etfFundId &&
        (profile.associatedFundTickers?.investorFundId ||
          profile.associatedFundTickers?.admiralFundId)
      ) {
        if (profile.associatedFundTickers.admiralFundId) {
          this.InvestorAdmiralShareSubTitle = ''
        } else {
          this.InvestorAdmiralShareSubTitle =
            this.alsoAvailableLinksInvestors.replace(
              'ticker',
              profile.associatedFundTickers.investorFundId.toLowerCase(),
            )
        }
      }
      this.associatedFundTickers(profile)
    }
  }
  //&nbsp;eslint-disable-next-line&nbsp;complexity,&nbsp;max-statements
  associatedFundTickers(profile: ProfileBody) {
    if (
      profile.associatedFundTickers?.etfFundId &&
      (profile.associatedFundTickers?.investorFundId ||
        profile.associatedFundTickers?.admiralFundId)
    ) {
      if (profile.associatedFundTickers.admiralFundId) {
        this.InvestorAdmiralShareSubTitle = this.alsoAvailableLinksETF.replace(
          'ticker',
          profile.associatedFundTickers.etfFundId.toLowerCase(),
        )
      } else {
        this.InvestorAdmiralShareSubTitle =
          this.alsoAvailableLinksEtfAndLowerInvestors
            .replace('tickerETF', profile.associatedFundTickers.etfFundId)
            .replace(
              'tickerInvestor',
              profile.associatedFundTickers.investorFundId,
            )
      }
    }
  }
  // eslint-disable-next-line complexity
  getETFAssociatedIDsSubTitle(profile): string {
    let subTitle = ''
    if (profile?.isETF && profile.associatedFundTickers?.investorFundId) {
      subTitle = this.alsoAvailableLinksInvestorsMutualFunds.replace(
        'ticker',
        profile.associatedFundTickers.investorFundId,
      )
    }
    if (profile?.isETF && profile.associatedFundTickers?.admiralFundId) {
      subTitle = this.alsoAvailableLinksAdmiral.replace(
        'ticker',
        profile.associatedFundTickers.admiralFundId.toLowerCase(),
      )
    }
    return subTitle
  }

  isInstitutionalPlusFundId(profile: ProfileBody) {
    return (
      profile?.fundFact?.isInstitutionalShare &&
      profile.associatedFundIds?.institutionalPlusFundId === profile.fundId
    )
  }
}
